import { getShareAllowedEmailDomainsForTenant } from '../../config';
import type { Question } from '../../config/types';
import { textareaMaxLength, textinputMaxLength } from '../../constants';
import { allowedDomainValidationMessage, emailMatchesAllowedDomains } from '../email-domain-utils';
import { parseEmailList } from '../email-list-utils';
import {
  getCharacterCount,
  getInputRules,
  getWordCount,
  hasValue,
  validateField,
} from '../form-utils';
import type { ListQuestionDescriptor } from '../list-question-utils';
import {
  getListQuestionOptionExclusionMessage,
  isListQuestionActive,
} from '../list-question-utils';
import { getListPageExcelColumns } from './columns';

export type ListPageExcelValidationError = {
  worksheetRow: number;
  columnIndex: number;
  fieldName: string;
  fieldTitle: string;
  message: string;
};

export type ListPageExcelValidatedRow = {
  worksheetRow: number;
  answers: Record<string, unknown>;
};

type ValidationResult = {
  value: unknown;
  message?: string;
};

const getCellScalar = (value: unknown): string | number | boolean | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  throw new Error('Remove formulas, links, dates, or other unsupported Excel cell values.');
};

export const getCellText = (value: unknown) => {
  const scalar = getCellScalar(value);
  return scalar === null ? '' : String(scalar).trim();
};

const validateWordAndCharacterCounts = (question: Question, value: string) => {
  if (typeof question.wordCount === 'number') {
    const wordsUsed = getWordCount(value);
    if (wordsUsed > question.wordCount) {
      const over = wordsUsed - question.wordCount;
      return `You have ${over} word${over === 1 ? '' : 's'} too many`;
    }
  }

  if (typeof question.characterCount === 'number') {
    const charsUsed = getCharacterCount(value);
    if (charsUsed > question.characterCount) {
      const over = charsUsed - question.characterCount;
      return `You have ${over} character${over === 1 ? '' : 's'} too many`;
    }
  }

  return undefined;
};

const validateText = (question: Question, rawValue: unknown, tenant: string): ValidationResult => {
  const value = getCellText(rawValue);
  const defaultMaxLength =
    question.questionType === 'textarea' ? textareaMaxLength : textinputMaxLength;
  const inputRules = getInputRules(question.rules || {}, question.title, defaultMaxLength);
  const baseError = validateField(question.title, inputRules, value, question.errorMessages);
  if (baseError) return { value, message: baseError };

  if (question.emailList && parseEmailList(value).hasInvalidEntries) {
    return { value, message: 'Check the email addresses in this cell' };
  }

  if (question.rules?.email && question.restrictDomains !== false && value) {
    const allowedDomains = getShareAllowedEmailDomainsForTenant(tenant);
    if (allowedDomains.length > 0 && !emailMatchesAllowedDomains(value, allowedDomains)) {
      return { value, message: allowedDomainValidationMessage(allowedDomains) };
    }
  }

  return { value, message: validateWordAndCharacterCounts(question, value) };
};

const validateNumber = (question: Question, rawValue: unknown): ValidationResult => {
  const value = getCellText(rawValue).replace(/[£,]/g, '').trim();
  const inputRules = getInputRules(question.rules || {}, question.title, textinputMaxLength);
  const baseError = validateField(question.title, inputRules, value, question.errorMessages);
  if (baseError) return { value, message: baseError };

  if (value && question.questionType === 'instanceInput' && !/^\d+$/.test(value)) {
    return {
      value,
      message: question.errorMessages?.number || 'Enter a whole number',
    };
  }

  if (
    value &&
    question.questionType === 'costInput' &&
    ((!question.rules?.decimal && !/^\d+$/.test(value)) || value.startsWith('-'))
  ) {
    return {
      value,
      message: question.errorMessages?.number || 'Enter a whole number',
    };
  }

  return { value };
};

const normaliseOptionText = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

const getMatchingOption = (descriptor: ListQuestionDescriptor, value: string) =>
  descriptor.options.find(
    (option) => option.label.trim() === value || option.value.trim() === value,
  );

const getNormalisedMatchingOption = (descriptor: ListQuestionDescriptor, value: string) => {
  const normalisedValue = normaliseOptionText(value);
  return descriptor.options.find(
    (option) =>
      normaliseOptionText(option.label) === normalisedValue ||
      normaliseOptionText(option.value) === normalisedValue,
  );
};

const parseCommaSeparatedSelections = (
  descriptor: ListQuestionDescriptor,
  value: string,
): string[] | undefined => {
  const parts = value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const matchesByStart = new Map<number, string[] | undefined>();

  const matchFrom = (start: number): string[] | undefined => {
    if (start === parts.length) return [];
    if (matchesByStart.has(start)) return matchesByStart.get(start);

    for (let end = parts.length; end > start; end -= 1) {
      const candidate = parts.slice(start, end).join(', ');
      const option = getNormalisedMatchingOption(descriptor, candidate);
      if (!option) continue;
      const remaining = matchFrom(end);
      if (remaining) {
        const matches = [option.value, ...remaining];
        matchesByStart.set(start, matches);
        return matches;
      }
    }

    matchesByStart.set(start, undefined);
    return undefined;
  };

  return matchFrom(0);
};

const parseMultiListSelections = (
  descriptor: ListQuestionDescriptor,
  value: string,
): string[] | undefined => {
  const groups = value
    .split(/[;\r\n]+/)
    .map((group) => group.trim())
    .filter(Boolean);
  const selections: string[] = [];

  for (const group of groups) {
    const matches = parseCommaSeparatedSelections(descriptor, group);
    if (!matches) return undefined;
    selections.push(...matches);
  }

  return selections;
};

const validateList = (
  descriptor: ListQuestionDescriptor,
  rawValue: unknown,
  rowAnswers: Record<string, unknown>,
): ValidationResult => {
  const value = getCellText(rawValue);
  const requiredError = validateField(
    descriptor.question.title,
    descriptor.question.rules || {},
    value,
    descriptor.question.errorMessages,
  );
  if (requiredError) return { value, message: requiredError };
  if (!value) return { value: '' };

  const option = getMatchingOption(descriptor, value);
  if (!option) {
    return {
      value,
      message: `Select a valid option for ${descriptor.question.title}`,
    };
  }
  const exclusionMessage = getListQuestionOptionExclusionMessage(
    descriptor,
    option.value,
    rowAnswers,
  );
  if (exclusionMessage) return { value: option.value, message: exclusionMessage };
  return { value: option.value };
};

const validateMultiList = (
  descriptor: ListQuestionDescriptor,
  rawValue: unknown,
): ValidationResult => {
  const text = Array.isArray(rawValue) ? rawValue.map(String).join('; ') : getCellText(rawValue);
  const values = parseMultiListSelections(descriptor, text);

  if (descriptor.question.rules?.required && values?.length === 0) {
    return {
      value: [],
      message:
        descriptor.question.errorMessages?.required || `${descriptor.question.title} is mandatory`,
    };
  }

  if (!values) {
    return {
      value: text,
      message: `Select only valid options for ${descriptor.question.title}; separate choices with commas, semicolons or new lines`,
    };
  }

  return { value: [...new Set(values)] };
};

const validateCell = (
  descriptor: ListQuestionDescriptor,
  rawValue: unknown,
  tenant: string,
  rowAnswers: Record<string, unknown>,
): ValidationResult => {
  switch (descriptor.kind) {
    case 'text':
      return validateText(descriptor.question, rawValue, tenant);
    case 'number':
      return validateNumber(descriptor.question, rawValue);
    case 'list':
      return validateList(descriptor, rawValue, rowAnswers);
    case 'multi-list':
      return validateMultiList(descriptor, rawValue);
    default:
      return {
        value: getCellText(rawValue),
        message: `${descriptor.question.title} uses unsupported field type ${descriptor.question.questionType}`,
      };
  }
};

export const validateListPageExcelRows = ({
  rows,
  descriptors,
  tenant,
}: {
  rows: Array<{ worksheetRow: number; values: unknown[] }>;
  descriptors: ListQuestionDescriptor[];
  tenant: string;
}): {
  rows: ListPageExcelValidatedRow[];
  errors: ListPageExcelValidationError[];
} => {
  const errors: ListPageExcelValidationError[] = [];
  const validatedRows: ListPageExcelValidatedRow[] = [];
  const columns = getListPageExcelColumns(descriptors);

  rows.forEach(({ worksheetRow, values }) => {
    const rowAnswers: Record<string, unknown> = {};
    const cellReadErrors = new Map<number, string>();

    columns.forEach((column, columnIndex) => {
      try {
        column.descriptors.forEach((descriptor) => {
          const preliminary = validateCell(descriptor, values[columnIndex], tenant, rowAnswers);
          rowAnswers[descriptor.question.name] = preliminary.value;
        });
      } catch (error) {
        cellReadErrors.set(
          columnIndex,
          error instanceof Error ? error.message : 'This cell value cannot be imported.',
        );
      }
    });

    columns.forEach((column, columnIndex) => {
      const descriptor = column.descriptors[0];
      const readError = cellReadErrors.get(columnIndex);
      if (readError) {
        errors.push({
          worksheetRow,
          columnIndex: columnIndex + 1,
          fieldName: descriptor.question.name,
          fieldTitle: descriptor.question.title,
          message: readError,
        });
        return;
      }

      const rawValue = values[columnIndex];
      const activeDescriptors = column.descriptors.filter((candidate) =>
        isListQuestionActive(candidate, rowAnswers),
      );
      if (activeDescriptors.length === 0) {
        if (hasValue(rawValue)) {
          errors.push({
            worksheetRow,
            columnIndex: columnIndex + 1,
            fieldName: descriptor.question.name,
            fieldTitle: descriptor.question.title,
            message: `${descriptor.question.title} must be blank because its conditional question is not selected`,
          });
        }
        column.descriptors.forEach((candidate) => {
          delete rowAnswers[candidate.question.name];
        });
        return;
      }

      if (activeDescriptors.length > 1) {
        errors.push({
          worksheetRow,
          columnIndex: columnIndex + 1,
          fieldName: descriptor.question.name,
          fieldTitle: descriptor.question.title,
          message: `${descriptor.question.title} matches more than one conditional option`,
        });
        return;
      }

      const activeDescriptor = activeDescriptors[0];
      column.descriptors.forEach((candidate) => {
        if (candidate !== activeDescriptor) delete rowAnswers[candidate.question.name];
      });
      const result = validateCell(activeDescriptor, rawValue, tenant, rowAnswers);
      rowAnswers[activeDescriptor.question.name] = result.value;
      if (result.message) {
        errors.push({
          worksheetRow,
          columnIndex: columnIndex + 1,
          fieldName: activeDescriptor.question.name,
          fieldTitle: activeDescriptor.question.title,
          message: result.message,
        });
      }
    });

    validatedRows.push({ worksheetRow, answers: rowAnswers });
  });

  return { rows: validatedRows, errors };
};
